/**
 * Hooks da aba Playlist de `/tvs/:id` — TH-53378 / M5.E7.S3.1.
 *
 * Wrappers TanStack Query dos 3 endpoints já entregues no backend
 * (TH-53373/53374/53375):
 *
 *   - GET    /tvs/:id/playlist            → useTVPlaylist
 *   - PUT    /tvs/:id/playlist            → usePutTVPlaylist
 *   - POST   /tvs/:id/playlist/auto       → usePlaylistAutoGen
 *
 * Decisões de UX cobertas aqui:
 *
 * 1) GET 404 quando a TV ainda não tem playlist. A página interpreta esse
 *    erro como "estado vazio — clique em Auto-gerar". Não tentamos criar
 *    via POST /playlists pra simplificar o fluxo no escopo desta issue:
 *    auto-gerar materializa a primeira playlist, e a partir daí o PUT
 *    edita normalmente.
 *
 * 2) Slides com `custom_art_id` voltam do backend sem `product_art`
 *    aninhado (o schema PlaylistSlideWithArt só anincia `product_art`).
 *    Pra renderizar thumb+nome desses slides, a UI faz join client-side
 *    com a lista de custom_arts (`useCustomArtsList`) — não precisamos
 *    de outro endpoint.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

export interface SlideArtRef {
  id: string;
  art_url: string | null;
  status: string;
  product_id: string | null;
  product_name: string | null;
}

export interface PlaylistSlideWithArt {
  id: string;
  playlist_id: string;
  product_art_id: string | null;
  custom_art_id: string | null;
  position: number;
  duration_ms: number;
  active: boolean;
  schedule_from: string | null;
  schedule_until: string | null;
  product_art: SlideArtRef | null;
}

export interface PlaylistDetailWithArts {
  id: string;
  tenant_id: string;
  tv_id: string;
  etag: string;
  updated_at: string;
  slides: PlaylistSlideWithArt[];
}

export interface PlaylistSlideInput {
  product_art_id?: string;
  custom_art_id?: string;
  position: number;
  duration_ms: number;
  active: boolean;
}

export interface PlaylistReplaceInput {
  slides: PlaylistSlideInput[];
}

/**
 * Erro do GET enriquecido com `status` pra a UI distinguir 404
 * (playlist ainda não existe → empty state) de 5xx (erro real).
 */
export class TVPlaylistError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "TVPlaylistError";
  }
}

async function fetchTVPlaylist(tvId: string): Promise<PlaylistDetailWithArts> {
  const res = await fetch(`/api/tvs/${tvId}/playlist`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new TVPlaylistError(res.status, body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as PlaylistDetailWithArts;
}

export function useTVPlaylist(
  tvId: string,
  enabled = true,
): UseQueryResult<PlaylistDetailWithArts, TVPlaylistError> {
  return useQuery<PlaylistDetailWithArts, TVPlaylistError>({
    queryKey: ["tv-playlist", tvId],
    queryFn: () => fetchTVPlaylist(tvId),
    // 404 é estado válido (playlist não existe) — não fica retentando.
    retry: (count, err) => err.status !== 404 && count < 2,
    staleTime: 30_000,
    enabled,
  });
}

async function putTVPlaylist(
  tvId: string,
  payload: PlaylistReplaceInput,
): Promise<PlaylistDetailWithArts> {
  const res = await fetch(`/api/tvs/${tvId}/playlist`, {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as PlaylistDetailWithArts;
}

export function usePutTVPlaylist(
  tvId: string,
): UseMutationResult<PlaylistDetailWithArts, Error, PlaylistReplaceInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PlaylistReplaceInput) => putTVPlaylist(tvId, payload),
    onSuccess: (data) => {
      qc.setQueryData(["tv-playlist", tvId], data);
    },
  });
}

async function postPlaylistAutoGen(
  tvId: string,
): Promise<PlaylistDetailWithArts> {
  const res = await fetch(`/api/tvs/${tvId}/playlist/auto`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as PlaylistDetailWithArts;
}

export function usePlaylistAutoGen(
  tvId: string,
): UseMutationResult<PlaylistDetailWithArts, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => postPlaylistAutoGen(tvId),
    onSuccess: (data) => {
      qc.setQueryData(["tv-playlist", tvId], data);
    },
  });
}
